// src/pages/meeting/GuestJoin.tsx
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Video, User, ArrowRight, Shield } from 'lucide-react';

const GuestJoin: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [guestName, setGuestName] = useState('');
  const [isJoining, setIsJoining] = useState(false);
  const [error, setError] = useState('');

  const handleJoin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!guestName.trim()) {
      setError('Lütfen adınızı girin');
      return;
    }

    if (guestName.trim().length < 2) {
      setError('İsim en az 2 karakter olmalı');
      return;
    }

    setIsJoining(true);
    setError('');

    // Misafir bilgisini sessionStorage'a kaydet
    sessionStorage.setItem('tbkmeet_guest', JSON.stringify({
      name: guestName.trim(),
      odaId: id,
      joinedAt: new Date().toISOString()
    }));

    // Toplantıya yönlendir
    navigate(`/meeting/${id}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      {/* Arka plan efektleri */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-600 rounded-2xl mb-4">
            <Video className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">TBKMeet</h1>
          <p className="text-gray-400">Toplantıya katılmak üzeresiniz</p>
        </div>

        {/* Katılım Formu */}
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-white/10 p-8">
          <div className="text-center mb-6">
            <h2 className="text-xl font-semibold text-white mb-2">Toplantıya Katıl</h2>
            <p className="text-gray-400 text-sm">
              Toplantı Kodu: <span className="text-purple-400 font-mono">{id}</span>
            </p>
          </div>

          <form onSubmit={handleJoin} className="space-y-6">
            {/* İsim Girişi */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Adınız
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={guestName}
                  onChange={(e) => {
                    setGuestName(e.target.value);
                    setError('');
                  }}
                  placeholder="Adınızı girin"
                  className="w-full pl-10 pr-4 py-3 bg-slate-700/50 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                  autoFocus
                  maxLength={50}
                />
              </div>
              {error && (
                <p className="mt-2 text-sm text-red-400">{error}</p>
              )}
            </div>

            {/* Katıl Butonu */}
            <button
              type="submit"
              disabled={isJoining}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-600/50 text-white font-semibold rounded-xl transition duration-200 disabled:cursor-not-allowed"
            >
              {isJoining ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Bağlanıyor...
                </>
              ) : (
                <>
                  Toplantıya Katıl
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          {/* Bilgi Notu */}
          <div className="mt-6 p-4 bg-slate-700/30 rounded-xl">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-gray-400">
                <p>Misafir olarak katılıyorsunuz. Hesap oluşturarak daha fazla özelliğe erişebilirsiniz.</p>
              </div>
            </div>
          </div>

          {/* Hesap Oluştur Linki */}
          <div className="mt-6 text-center">
            <p className="text-gray-400 text-sm">
              Hesabınız yok mu?{' '}
              <a 
                href="/register" 
                className="text-purple-400 hover:text-purple-300 font-medium"
              >
                Kayıt ol
              </a>
            </p>
            <p className="text-gray-400 text-sm mt-2">
              Zaten hesabınız var mı?{' '}
              <a 
                href="/login" 
                className="text-purple-400 hover:text-purple-300 font-medium"
              >
                Giriş yap
              </a>
            </p>
          </div>
        </div>

        {/* Alt bilgi */}
        <p className="text-center text-gray-500 text-xs mt-6">
          Katılarak Kullanım Şartlarını ve Gizlilik Politikasını kabul etmiş olursunuz.
        </p>
      </div>
    </div>
  );
};

export default GuestJoin;